// ============================================================
// xScan - Nivel de validacion de un hallazgo
// ============================================================
// Es la distincion entre "vimos un indicador" y "lo explotamos y aqui esta la
// prueba". Un carrier no puede tarifar con lo primero; con lo segundo si.
//
// Vocabulario espejado de VALIDATION_LEVELS en
// api/src/scanners/reports/generator.ts — misma etiqueta y misma descripcion,
// para que el reporte y la UI no digan cosas distintas del mismo dato.
// ============================================================
import { Search, CheckCircle2, Crosshair } from 'lucide-react';

export type ValidationLevel = 'detected' | 'validated' | 'exploited';

const LEVELS: Record<
  ValidationLevel,
  { label: string; description: string; confidence: string; cls: string; icon: typeof Search }
> = {
  detected: {
    label: 'Detectado',
    description: 'Indicador identificado mediante analisis pasivo o fingerprinting',
    confidence: 'Media',
    cls: 'bg-gray-100 text-gray-600 border-gray-300',
    icon: Search,
  },
  validated: {
    label: 'Validado',
    description: 'Vulnerabilidad confirmada mediante pruebas activas con respuesta del servidor',
    confidence: 'Alta',
    cls: 'bg-amber-50 text-amber-700 border-amber-300',
    icon: CheckCircle2,
  },
  exploited: {
    label: 'Explotado',
    description: 'Explotacion exitosa demostrada con evidencia concreta de impacto',
    confidence: 'Muy Alta',
    cls: 'bg-red-50 text-red-700 border-red-300',
    icon: Crosshair,
  },
};

function normalize(level?: string | null): ValidationLevel {
  return level === 'validated' || level === 'exploited' ? level : 'detected';
}

interface Props {
  level?: string | null;
  /**
   * 'compact' se usa en listas: NO renderiza nada para 'detected'.
   * De 3287 hallazgos en produccion, 3191 son 'detected' — marcarlos todos
   * seria ruido y taparia justamente los 96 que si estan elevados.
   * 'full' siempre renderiza, con la descripcion, para la vista expandida.
   */
  variant?: 'compact' | 'full';
}

export default function ValidationBadge({ level, variant = 'compact' }: Props) {
  const key = normalize(level);
  const meta = LEVELS[key];

  if (variant === 'compact' && key === 'detected') return null;

  const Icon = meta.icon;

  if (variant === 'compact') {
    return (
      <span
        className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${meta.cls}`}
        title={`${meta.description} — Confianza: ${meta.confidence}`}
      >
        <Icon className="h-3 w-3" />
        {meta.label}
      </span>
    );
  }

  return (
    <div className={`rounded-lg border p-3 ${meta.cls}`}>
      <div className="flex items-center gap-2 text-sm font-semibold">
        <Icon className="h-4 w-4" />
        Nivel de validacion: {meta.label}
        <span className="ml-auto text-xs font-normal opacity-80">
          Confianza: {meta.confidence}
        </span>
      </div>
      <p className="mt-1 text-xs opacity-90">{meta.description}</p>
    </div>
  );
}
